import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import { OrganizationSetting } from "@mapmycustomers/shared";
import Currency from "@mapmycustomers/shared/types/Currency";
import MonetaryValue from "@mapmycustomers/shared/types/customField/MonetaryValue";
import { isDefined } from "@mapmycustomers/shared/util/assert";
import { OptionalFields } from "@mapmycustomers/shared/util/ts";
import { LabeledProps, MonetaryField, MonetaryFieldProps } from "@mapmycustomers/ui";

import { getOrganizationSettingValue } from "@app/store/iam";
import { getCurrencies } from "@app/store/referenceData";
import { RootState } from "@app/store/rootReducer";

interface Props
  extends Omit<LabeledProps, "children">, Pick<MonetaryFieldProps, "max" | "min" | "size"> {
  allowEmpty?: boolean;
  currencies: Currency[];
  defaultCurrencyCode?: string;
  disabled?: boolean;
  inputClassName?: string;
  onChange?: (value: number | undefined) => void;
  placeholder?: string;
  step?: number;
  value?: number;
}

const CurrencyInput = ({
  allowEmpty,
  currencies,
  defaultCurrencyCode,
  disabled,
  inputClassName,
  max,
  min,
  onChange,
  placeholder,
  size,
  step,
  value,
  ...labeledProps
}: Props) => {
  const intl = useIntl();

  const defaultCurrency = useMemo(
    () => currencies.find((currency) => currency.code === defaultCurrencyCode),
    [currencies, defaultCurrencyCode]
  );

  const [amount, setAmount] = useState<number | undefined>(value);
  useEffect(() => {
    setAmount(value);
  }, [value, defaultCurrency]);

  const handleChange = useCallback(
    (amount?: number) => {
      if (allowEmpty || isDefined(amount)) {
        onChange?.(amount);
      }
    },
    [allowEmpty, onChange]
  );

  const handleAmountChange = useCallback(
    (value: OptionalFields<MonetaryValue, "value">) => {
      setAmount(value.value);
      handleChange(value.value);
    },
    [handleChange]
  );

  return (
    <MonetaryField
      {...labeledProps}
      allowEmpty={allowEmpty}
      disabled={disabled}
      inputClassName={inputClassName}
      max={max}
      min={min}
      onChange={handleAmountChange}
      placeholder={intl.formatMessage({
        id: "component.currencyInput.placeholder",
        defaultMessage: "Enter amount",
        description: "Placeholder for currency field",
      })}
      size={size}
      step={step}
      value={
        !defaultCurrency || amount === undefined
          ? undefined
          : { currencyId: defaultCurrency.id, value: amount }
      }
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  currencies: getCurrencies(state),
  defaultCurrencyCode: getOrganizationSettingValue(state, OrganizationSetting.CURRENCY),
});

export default memo(connect(mapStateToProps)(CurrencyInput));
